import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, Globe } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import logo from "@/assets/affarsnatverket-logo-new.png";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navigation = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hasNavigatedWithHash, setHasNavigatedWithHash] = useState(false);
  const { isAdmin, isBroker, loading } = useAuth();
  
  const showBrokerButton = isAdmin || isBroker;
  const showAdminButton = isAdmin;

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("language", lng);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    // Only scroll to hash if user actively navigated there
    if (location.pathname === '/' && location.hash && hasNavigatedWithHash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
        setHasNavigatedWithHash(false);
      }, 100);
    }
  }, [location, hasNavigatedWithHash]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setIsMobileMenuOpen(false);
    }
  };

  const handleNavigation = (sectionId: string) => {
    if (location.pathname === '/') {
      // Already on homepage, scroll directly
      scrollToSection(sectionId);
    } else {
      // On another page, navigate to homepage with hash
      setHasNavigatedWithHash(true);
      navigate(`/#${sectionId}`);
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isMobileMenuOpen ? "bg-card shadow-lg" : isScrolled ? "bg-card/95 backdrop-blur-sm shadow-card" : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center space-x-2">
            <img 
              src={logo} 
              alt="Affärsnätverket" 
              width="258" 
              height="62" 
              className="h-[62px] w-auto cursor-pointer"
              loading="eager"
              fetchPriority="high"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/foretagsnatverk" className="min-h-[44px] flex items-center">
              <span className="text-foreground/80 hover:text-foreground transition-colors text-xs font-normal">
                Företagsnätverk
              </span>
            </Link>
            <Link to="/kalender" className="min-h-[44px] flex items-center">
              <span className="text-foreground/80 hover:text-foreground transition-colors text-xs font-normal">
                Kalender
              </span>
            </Link>
            <Link to="/pris" className="min-h-[44px] flex items-center">
              <span className="text-foreground/80 hover:text-foreground transition-colors text-xs font-normal">
                Pris
              </span>
            </Link>
            <Link to="/news" className="min-h-[44px] flex items-center">
              <span className="text-foreground/80 hover:text-foreground transition-colors text-xs font-normal">
                Nyheter
              </span>
            </Link>
            <Link to="/om-oss" className="min-h-[44px] flex items-center">
              <span className="text-foreground/80 hover:text-foreground transition-colors text-xs font-normal">
                Om oss
              </span>
            </Link>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center space-x-1 text-foreground/60 hover:text-foreground/80 transition-colors text-sm">
                  <Globe className="w-4 h-4" />
                  <span className="text-xs">{i18n.language === "sv" ? "SV" : "EN"}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-card border-border shadow-lg z-50 min-w-[140px]">
                <DropdownMenuItem onClick={() => changeLanguage("sv")} className="cursor-pointer">
                  🇸🇪 Svenska
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeLanguage("en")} className="cursor-pointer">
                  🇬🇧 English
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Mäklare knapp - synlig för både admin och mäklare */}
            {showBrokerButton && (
              <Link to="/broker">
                <Button variant="outline" size="sm">
                  Mäklare
                </Button>
              </Link>
            )}
            
            {/* Admin knapp - synlig endast för admin */}
            {showAdminButton && (
              <Link to="/admin">
                <Button variant="outline" size="sm">
                  Admin
                </Button>
              </Link>
            )}
            
            {/* Om inte inloggad, visa Login knapp */}
            {!loading && !isAdmin && !isBroker && (
              <Link to="/auth">
                <Button variant="ghost" size="sm" className="text-foreground/40 hover:text-foreground/50 text-xs">
                  Logga in
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-foreground p-2 -mr-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-muted/50 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Stäng meny" : "Öppna meny"}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden pb-6 pt-2 animate-fade-in border-t border-border/50 bg-card">
            <div className="flex flex-col space-y-1">
              {/* Navigation Links */}
              <Link
                to="/foretagsnatverk"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-foreground hover:text-primary hover:bg-primary/5 transition-colors font-medium text-left min-h-[48px] flex items-center px-3 rounded-lg w-full"
              >
                Företagsnätverk
              </Link>
              <Link
                to="/kalender"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-foreground hover:text-primary hover:bg-primary/5 transition-colors font-medium text-left min-h-[48px] flex items-center px-3 rounded-lg w-full"
              >
                Kalender
              </Link>
              <Link
                to="/pris"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-foreground hover:text-primary hover:bg-primary/5 transition-colors font-medium text-left min-h-[48px] flex items-center px-3 rounded-lg w-full"
              >
                Pris
              </Link>
              <Link
                to="/news"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-foreground hover:text-primary hover:bg-primary/5 transition-colors font-medium text-left min-h-[48px] flex items-center px-3 rounded-lg w-full"
              >
                Nyheter
              </Link>
              <Link
                to="/om-oss"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-foreground hover:text-primary hover:bg-primary/5 transition-colors font-medium text-left min-h-[48px] flex items-center px-3 rounded-lg w-full"
              >
                Om oss
              </Link>

              
              {/* Separator */}
              <div className="border-t border-border/50 my-2" />
              
              {/* Language Selector - Inline buttons */}
              <div className="flex items-center gap-2 py-2 px-3">
                <Globe className="w-4 h-4 text-muted-foreground" />
                <button
                  onClick={() => { changeLanguage("sv"); setIsMobileMenuOpen(false); }}
                  className={`text-sm px-3 py-1.5 rounded-md transition-colors ${
                    i18n.language === "sv" ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Svenska
                </button>
                <button
                  onClick={() => { changeLanguage("en"); setIsMobileMenuOpen(false); }}
                  className={`text-sm px-3 py-1.5 rounded-md transition-colors ${
                    i18n.language === "en" ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  English
                </button>
              </div>
              
              {/* Separator */}
              <div className="border-t border-border/50 my-2" />
              
              {/* CTA Buttons */}
              <div className="flex flex-col gap-2 px-2">
                {/* Login knapp om inte inloggad */}
                {!loading && !isAdmin && !isBroker && (
                  <Link to="/auth" onClick={() => setIsMobileMenuOpen(false)} className="w-full">
                    <Button variant="outline" className="w-full min-h-[48px]">
                      Logga in
                    </Button>
                  </Link>
                )}
                
                {/* Mäklare knapp */}
                {showBrokerButton && (
                  <Link to="/broker" onClick={() => setIsMobileMenuOpen(false)} className="w-full">
                    <Button variant="outline" className="w-full min-h-[48px]">
                      Mäklare
                    </Button>
                  </Link>
                )}
                
                {/* Admin knapp */}
                {showAdminButton && (
                  <Link to="/admin" onClick={() => setIsMobileMenuOpen(false)} className="w-full">
                    <Button variant="outline" className="w-full min-h-[48px]">
                      Admin
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
